import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { LuCheck, LuChevronLeft, LuSearch } from 'react-icons/lu'
import { cn } from '../../../lib/cn.ts'

export interface OptionItem {
  id: string
  /** Plain text: what search matches and what screen readers hear. */
  label: string
  /** Rendered instead of `label` (e.g. a lozenge or a chip); keep `label` in sync. */
  render?: ReactNode
  /** Leading visual (icon, dot, avatar). */
  leading?: ReactNode
  /** Trailing text, e.g. "2 selected" or "archived". */
  meta?: ReactNode
  /** Extra words search should match. */
  keywords?: string
  /** Shows a check (multi-select lists show a checkbox for every row). */
  selected?: boolean
  /** Opens a sub-view rather than picking a value (shows a chevron). */
  submenu?: boolean
}

export interface OptionListProps {
  items: OptionItem[]
  onPick: (id: string) => void
  /** Accessible name of the listbox ("Filter fields", "Priority"). */
  label: string
  placeholder?: string
  /** Checkbox rows and `aria-multiselectable`. */
  multi?: boolean
  /** Shown above the search as a back link (value lists opened from the field list). */
  onBack?: () => void
  backLabel?: string
  /** Backspace in an empty search box (Linear-style "back"). */
  onEmptyBackspace?: () => void
  emptyText?: string
  /** Extra content under the list (e.g. a custom date range form). */
  footer?: ReactNode
  autoFocus?: boolean
}

/**
 * Searchable list for popovers: the search box keeps focus and arrow keys move the active row
 * (`aria-activedescendant`), Enter picks it, Home/End jump. Used for the "Add filter" field list
 * and each field's values.
 */
export function OptionList({
  items,
  onPick,
  label,
  placeholder = 'Search',
  multi,
  onBack,
  backLabel = 'Back',
  onEmptyBackspace,
  emptyText = 'No matches',
  footer,
  autoFocus = true,
}: OptionListProps) {
  const id = useId()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const pointer = useRef<{ x: number; y: number } | null>(null)
  // Focus after the popover has positioned itself (autoFocus could scroll the page).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus({ preventScroll: true })
  }, [])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((i) => `${i.label} ${i.keywords ?? ''}`.toLowerCase().includes(q))
  }, [items, query])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the query changes
  useEffect(() => setActive(0), [query])
  const current = Math.min(active, Math.max(0, shown.length - 1))
  const optionId = (i: number) => `${id}-opt-${i}`

  // Keep the active row in view (scrolling only the list: `scrollIntoView` could move the page).
  useEffect(() => {
    const list = listRef.current
    const el = list?.querySelector<HTMLElement>(`[id="${CSS.escape(optionId(current))}"]`)
    if (!list || !el) return
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop - 4
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight + 4
  })

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = shown.length
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (n) setActive((a) => (Math.min(a, n - 1) + 1) % n)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (n) setActive((a) => (Math.min(a, n - 1) - 1 + n) % n)
        break
      case 'Home':
        if (n && !query) {
          e.preventDefault()
          setActive(0)
        }
        break
      case 'End':
        if (n && !query) {
          e.preventDefault()
          setActive(n - 1)
        }
        break
      case 'Enter': {
        e.preventDefault()
        const item = shown[current]
        if (item) onPick(item.id)
        break
      }
      case 'Backspace':
        if (!query && onEmptyBackspace) {
          e.preventDefault()
          onEmptyBackspace()
        }
        break
    }
  }

  return (
    <div className="flex min-h-0 flex-col">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 border-b border-border px-2.5 py-1.5 text-left text-[12.5px] font-medium text-muted hover:bg-surface-2 hover:text-text"
        >
          <LuChevronLeft aria-hidden className="size-3.5" />
          {backLabel}
        </button>
      )}
      <div className="relative border-b border-border">
        <LuSearch
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-faint"
        />
        <input
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={`${id}-list`}
          aria-activedescendant={shown.length ? optionId(current) : undefined}
          aria-autocomplete="list"
          aria-label={`Search ${label.toLowerCase()}`}
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-9 w-full bg-transparent pr-2.5 pl-8 text-[13px] text-text placeholder:text-faint focus:outline-none"
        />
      </div>
      <div
        ref={listRef}
        id={`${id}-list`}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multi || undefined}
        className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
      >
        {shown.length === 0 && <p className="px-2.5 py-2 text-[13px] text-muted">{emptyText}</p>}
        {shown.map((item, i) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by the combobox input
          <div
            key={item.id}
            id={optionId(i)}
            role="option"
            aria-selected={!!item.selected}
            tabIndex={-1}
            onMouseMove={(e) => {
              // Only a real pointer move: browsers also fire mousemove when rows shift under a
              // resting cursor, which would steal the keyboard's active row.
              const last = pointer.current
              pointer.current = { x: e.clientX, y: e.clientY }
              if (last && last.x === e.clientX && last.y === e.clientY) return
              if (i !== current) setActive(i)
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(item.id)}
            className={cn(
              'flex min-h-8 cursor-pointer items-center gap-2 rounded-input px-2 py-1 text-[13px] select-none',
              i === current && 'bg-surface-2',
            )}
          >
            {multi && (
              <span
                aria-hidden
                className={cn(
                  'flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border',
                  item.selected
                    ? 'border-accent bg-accent text-accent-ink'
                    : 'border-border-strong bg-surface',
                )}
              >
                {item.selected && <LuCheck className="size-2.5" strokeWidth={3.5} />}
              </span>
            )}
            {item.leading && (
              <span className="flex w-4 shrink-0 items-center justify-center [&_svg]:size-4">
                {item.leading}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate">{item.render ?? item.label}</span>
            {item.meta && <span className="shrink-0 text-xs text-muted">{item.meta}</span>}
            {!multi && item.selected && (
              <LuCheck aria-hidden className="size-3.5 shrink-0 text-accent" />
            )}
            {item.submenu && !item.selected && (
              <LuChevronLeft aria-hidden className="size-3.5 shrink-0 rotate-180 text-faint" />
            )}
          </div>
        ))}
      </div>
      {footer}
    </div>
  )
}
